import React, { useEffect, useState, RefObject } from 'react';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { shuffle } from 'lodash';

import Button from 'components/designSystem/Button/Button';

import { FormContext, OrderAgain } from './ratingsSurveyModalTypes';

import Textarea from 'components/designSystem/Textarea/Textarea';

import styles from './RatingsSurveyModal.module.scss';

const OA = OrderAgain;

interface OrderAgainFollowUpQuestionProps {
  orderAgainResponse: OrderAgain;
  formContext: FormContext;
  questionRef: RefObject<HTMLDivElement>;
  otherButtonRef: RefObject<HTMLDivElement>;
}

interface OrderAgainFollowUpOtherProps {
  formContext: FormContext;
  buttonRef: RefObject<HTMLDivElement>;
}

interface PromptOption {
  choices: Array<string>;
  questionText: string;
}

function OrderAgainFollowUpOther({ formContext, buttonRef }: OrderAgainFollowUpOtherProps) {
  const { register, setValue, watch } = formContext;

  const formAttrs = register('orderAgainFollowUpOther');
  const value = watch('orderAgainFollowUpOther');

  function handleChange(value: string) {
    setValue('orderAgainFollowUpOther', value);
  }

  return (
    <div ref={buttonRef}>
      <Textarea
        {...formAttrs}
        onChange={handleChange}
        className={styles.otherInput}
        dataTestId="OrderAgainFollowUpOther"
        id="orderAgainFollowUpOther"
        placeholder="Share any additional feedback here"
        label=""
        shouldHideLabel
        value={value}
      />
    </div>
  );
}

export default function OrderAgainFollowUpQuestion({
  orderAgainResponse,
  formContext,
  questionRef,
  otherButtonRef,
}: OrderAgainFollowUpQuestionProps) {
  // Record<K, V> utility type:
  // https://www.typescriptlang.org/docs/handbook/utility-types.html#recordkeys-type
  const promptOptions: Record<OrderAgain, PromptOption> = {
    [OA.Yes]: {
      questionText: 'Great! What did you like?',
      choices: [
        'Tasty food',
        'Good value',
        'Food was fresh',
        'Followed instructions',
        'Ready on time',
      ],
    },
    [OA.No]: {
      questionText: "What didn't you like?",
      choices: ['Not tasty', 'Not a good value', 'Food was cold', 'Inaccurate order', 'Long wait'],
    },
    [OA.Maybe]: {
      questionText: 'What makes you unsure?',
      choices: [
        'Taste of food',
        'Not a good value',
        'Food was cold',
        'Inaccurate order',
        'Long wait',
      ],
    },
  };
  const { trigger, register, setValue, watch } = formContext;
  const { questionText, choices: rawChoices } = promptOptions[orderAgainResponse];

  const [choices, setChoices] = useState<Array<string>>([]);
  const selectedChoices = watch('orderAgainFollowUp') || [];

  const otherIsSelected = selectedChoices.some((choice) => choice === 'Other');

  useEffect(() => {
    register('orderAgainFollowUp', { validate });
  }, []);

  useEffect(() => {
    if (!otherIsSelected) {
      return;
    }

    // Without a way to set # of rows or set a ref on the textarea
    // behind this <Input /> we need to do this the Old Fashioned Way
    const otherInput = document.getElementById('orderAgainFollowUpOther') as HTMLTextAreaElement;
    otherInput.rows = 3;
  }, [otherIsSelected]);

  useEffect(() => {
    // randomize choice order when a new "order again" response is selected
    const newChoices = shuffle(rawChoices).concat(['Other']);
    setChoices(newChoices);

    // also clear choices the user may have previously selected
    setValue('orderAgainFollowUp', undefined);
    setValue('orderAgainFollowUpOther', undefined);
  }, [orderAgainResponse]);

  function choiceIsSelected(choice: string): boolean {
    return selectedChoices.includes(choice);
  }

  function handleChoiceClicked(choice: string): void {
    const newSelectedChoices = selectedChoices.includes(choice)
      ? selectedChoices.filter((selectedChoice) => selectedChoice !== choice)
      : selectedChoices.concat(choice);

    setValue('orderAgainFollowUp', newSelectedChoices);
    trigger('orderAgainFollowUp');
  }

  function validate(choices: Array<string>): boolean {
    return !isEmpty(choices);
  }

  return (
    <div
      ref={questionRef}
      className={classNames(styles.question, styles.pills)}
      data-testid="OrderAgainFollowUpQuestion"
    >
      <h3 className={styles.questionText}>{questionText}</h3>
      <div className={styles.choices}>
        {choices.map((choice) => (
          <div key={choice}>
            <Button
              className={classNames(styles.choice, {
                [styles.selected]: choiceIsSelected(choice),
              })}
              variant="outline"
              onClick={() => handleChoiceClicked(choice)}
            >
              {choice}
            </Button>
          </div>
        ))}
      </div>
      {otherIsSelected && (
        <OrderAgainFollowUpOther buttonRef={otherButtonRef} formContext={formContext} />
      )}
    </div>
  );
}
